<template>
  <main class="mcp-auth">
    <section class="mcp-auth__story">
      <div class="mcp-auth__glow mcp-auth__glow--blue" />
      <div class="mcp-auth__glow mcp-auth__glow--pink" />
      <div class="mcp-auth__glow mcp-auth__glow--yellow" />

      <div class="mcp-auth__story-content">
        <a href="https://www.measure.studio/" class="mcp-auth__brand" aria-label="Measure Studio">
          <img :src="wordmarkPath" alt="Measure Studio">
        </a>

        <div class="mcp-auth__copy">
          <div class="mcp-auth__eyebrow">
            <span class="mcp-auth__status-dot" />
            {{ $t('oauth.mcp.eyebrow') }}
          </div>
          <h1 class="mcp-auth__title">
            {{ $t('oauth.mcp.title', { client: mcpClientName }) }}
          </h1>
          <p class="mcp-auth__description">
            {{ $t('oauth.mcp.description', { client: mcpClientName }) }}
          </p>
        </div>

        <div class="mcp-auth__conversation" aria-hidden="true">
          <div class="mcp-auth__conversation-header">
            <span class="mcp-auth__conversation-status" />
            {{ $t('oauth.mcp.connected', { client: mcpClientName }) }}
          </div>

          <div class="mcp-auth__conversation-body">
            <div
              v-for="(conversation, index) in conversations"
              :key="conversation.prompt"
              class="mcp-auth__conversation-turn"
              :style="{ animationDelay: `${index * 6}s` }"
            >
              <div class="mcp-auth__message mcp-auth__message--question">
                {{ $t(conversation.prompt) }}
              </div>
              <div class="mcp-auth__message mcp-auth__message--answer">
                <span class="mcp-auth__message-mark">M</span>
                <span>{{ $t(conversation.answer) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="mcp-auth__value-props">
          <p class="mcp-auth__value-props-title">
            {{ $t('oauth.mcp.value_props_title') }}
          </p>
          <ul>
            <li>
              <span class="mcp-auth__check">✓</span>
              {{ $t('oauth.mcp.creative_value') }}
            </li>
            <li>
              <span class="mcp-auth__check">✓</span>
              {{ $t('oauth.mcp.patterns_value') }}
            </li>
            <li>
              <span class="mcp-auth__check">✓</span>
              {{ $t('oauth.mcp.targets_value') }}
            </li>
          </ul>
        </div>

        <div class="mcp-auth__story-footer">
          <span>{{ $t('oauth.mcp.works_with') }}</span>
          <a href="https://www.measure.studio/" target="_blank" rel="noopener noreferrer">
            {{ $t('oauth.mcp.learn_more') }} <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </section>

    <section class="mcp-auth__access">
      <div class="mcp-auth__access-content">
        <div class="mcp-auth__mobile-brand">
          <img :src="wordmarkPath" alt="Measure Studio">
        </div>

        <div class="mcp-auth__trial">
          <h2>{{ $t('oauth.mcp.new_to_measure') }}</h2>
          <p>{{ $t('oauth.mcp.trial_description') }}</p>
          <RouterLink
            :to="{
              name: 'register_by_params',
              params: { referrerLink: 'mcp' },
              query: $route.query,
            }"
            class="mcp-auth__trial-button"
          >
            {{ $t('oauth.mcp.start_trial') }}
            <span aria-hidden="true">→</span>
          </RouterLink>
        </div>

        <div class="mcp-auth__sign-in">
          <div class="mcp-auth__form-heading">
            <h3>{{ $t('oauth.mcp.sign_in_title') }}</h3>
            <p>{{ $t('oauth.mcp.sign_in_description', { client: mcpClientName }) }}</p>
          </div>

          <div class="overlay__form-wrapper mcp-auth__login-form">
            <LoginForm
              :return-query="$route.query"
              registration-referrer="mcp"
              :show-registration-prompt="false"
              submit-button-variant="secondary"
              @success="loginSuccess"
            />
          </div>
        </div>

        <div class="mcp-auth__legal">
          <a href="https://www.measure.studio/privacy-policy/" target="_blank" rel="noopener noreferrer">
            {{ $t('layout.footer.privacy') }}
          </a>
          <span>·</span>
          <a href="https://www.measure.studio/terms-of-service/" target="_blank" rel="noopener noreferrer">
            {{ $t('layout.footer.tos') }}
          </a>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import LoginForm from '../../elements/auth/login/LoginForm.vue';
import wordmarkPath from '../../../assets/images/logos/measure-studio-wordmark-color.svg';
import buildAuthRedirectUrl from '../../../utils/auth_redirect';
import { normalizeMcpClient } from '../../../utils/mcp_client';
import { rememberMcpOnboarding } from '../../../utils/mcp_onboarding';

export default {
  components: {
    LoginForm,
  },
  data() {
    return {
      wordmarkPath,
      conversations: [
        {
          prompt: 'oauth.mcp.strategy_prompt',
          answer: 'oauth.mcp.strategy_answer',
        },
        {
          prompt: 'oauth.mcp.nike_prompt',
          answer: 'oauth.mcp.nike_answer',
        },
        {
          prompt: 'oauth.mcp.format_prompt',
          answer: 'oauth.mcp.format_answer',
        },
        {
          prompt: 'oauth.mcp.posting_time_prompt',
          answer: 'oauth.mcp.posting_time_answer',
        },
        {
          prompt: 'oauth.mcp.report_prompt',
          answer: 'oauth.mcp.report_answer',
        },
      ],
    };
  },
  computed: {
    mcpClient() {
      return normalizeMcpClient(this.$route.query.mcp_client);
    },
    mcpClientName() {
      return this.$t(`oauth.mcp.clients.${this.mcpClient}`);
    },
  },
  created() {
    if (this.$gtag) {
      this.$gtag.event('mcp_sign_in_page_view', {
        event_category: 'mcp_auth_flow',
        mcp_client: this.mcpClient,
        entry_point: this.$route.query.email_confirmed ? 'email_confirmation' : 'connector',
      });
    }

    if (this.$route.query.email_confirmed) {
      rememberMcpOnboarding(this.mcpClient);

      if (this.$gtag) {
        this.$gtag.event('email_confirmed', { event_category: 'registration_flow' });
      }
    }

    if (this.$route.query.message) {
      this.$quickNotice.notify({
        type: 'success',
        body: this.$route.query.message,
      });
    }
  },
  methods: {
    loginSuccess(response) {
      if (this.$gtag && response.signInCount === 1) {
        this.$gtag.event('first_login', { event_category: 'registration_flow' });
      }

      window.location = this.redirectUrl();
    },
    redirectUrl() {
      return buildAuthRedirectUrl(this.$route.query.redirect);
    },
  },
};
</script>

<style lang="scss" scoped>
.mcp-auth {
  @apply grid min-h-screen w-full bg-white;

  grid-template-columns: minmax(0, 1.08fr) minmax(480px, 0.92fr);

  &__story {
    @apply relative overflow-hidden text-neutral-900;

    background: linear-gradient(145deg, #fbfcff 0%, #edf3ff 52%, #fff6fb 100%);
  }

  &__glow {
    @apply pointer-events-none absolute rounded-full;

    filter: blur(80px);

    &--blue {
      width: 520px;
      height: 520px;
      left: -300px;
      top: 17%;
      background: rgb(49 82 220 / 34%);
    }

    &--pink {
      width: 420px;
      height: 420px;
      right: -240px;
      top: -190px;
      background: rgb(255 35 115 / 25%);
    }

    &--yellow {
      width: 360px;
      height: 360px;
      right: 12%;
      bottom: -230px;
      background: rgb(255 196 46 / 28%);
    }
  }

  &__story-content {
    @apply relative z-[1] mx-auto flex min-h-screen w-full max-w-[720px] flex-col box-border;

    padding: 48px clamp(40px, 6vw, 84px) 40px;
  }

  &__brand img,
  &__mobile-brand img {
    @apply block h-[25px] w-auto;
  }

  &__copy {
    @apply mt-auto pt-[58px];
  }

  &__eyebrow {
    @apply mb-5 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-info-900;
  }

  &__status-dot {
    @apply relative block h-2 w-2 rounded-full bg-success-600;

    box-shadow: 0 0 0 4px rgb(55 210 115 / 14%);
  }

  &__title {
    @apply m-0 max-w-[620px] text-[46px] font-bold leading-[1.08] tracking-[-0.025em] text-neutral-1000;
  }

  &__description {
    @apply mt-5 max-w-[620px] text-lg leading-[1.7] text-neutral-700;
  }

  &__conversation {
    @apply relative mt-9 overflow-hidden rounded-xl border border-white/80 p-5;

    background: rgb(255 255 255 / 56%);
    box-shadow:
      0 28px 72px -38px rgb(49 82 220 / 56%),
      30px -24px 68px -46px rgb(255 35 115 / 55%),
      -24px 30px 62px -48px rgb(255 196 46 / 72%),
      inset 0 1px 0 rgb(255 255 255 / 92%);
    backdrop-filter: blur(24px) saturate(135%);
  }

  &__conversation-header {
    @apply mb-4 flex items-center gap-2 text-xs font-semibold text-neutral-700;
  }

  &__conversation-status {
    @apply h-2 w-2 rounded-full bg-success-600;
  }

  &__conversation-body {
    @apply relative h-[154px];
  }

  &__conversation-turn {
    @apply absolute inset-0 opacity-0;

    animation: conversation-cycle 30s ease-in-out infinite;
  }

  &__message {
    @apply max-w-[90%] rounded-xl border px-4 py-3 text-md leading-6 text-neutral-800 shadow-lifted;

    &--question {
      @apply ml-auto rounded-br-sm border-white/90;

      background: rgb(255 255 255 / 82%);
    }

    &--answer {
      @apply mt-3 flex items-start gap-3 rounded-bl-sm border-neutral-200/80;

      background: rgb(240 244 248 / 76%);
    }
  }

  &__message-mark {
    @apply mt-[1px] flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-info-200 bg-white;
    @apply text-xs font-bold text-info-900;
  }

  &__value-props {
    @apply mt-7;
  }

  &__value-props-title {
    @apply mb-3 text-lg font-semibold text-neutral-900;
  }

  &__value-props ul {
    @apply m-0 flex flex-col gap-2 p-0;
  }

  &__value-props li {
    @apply flex items-start gap-2 text-sm leading-5 text-neutral-700;
  }

  &__check {
    @apply mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white/80 bg-white/60;
    @apply text-xs font-bold text-success-800;
  }

  &__story-footer {
    @apply mt-auto flex items-center justify-between gap-5 border-t border-white/60 pt-6 text-xs text-neutral-700;
  }

  &__story-footer a {
    @apply shrink-0 font-semibold text-info-900;

    &:hover,
    &:focus {
      @apply text-info-1000;
    }
  }

  &__access {
    @apply flex items-center justify-center bg-white px-[clamp(32px,6vw,92px)] py-12;
  }

  &__access-content {
    @apply w-full max-w-[404px];
  }

  &__mobile-brand {
    @apply hidden;
  }

  &__trial h2 {
    @apply m-0 text-[30px] font-semibold leading-[1.2] tracking-[-0.02em] text-neutral-900;
  }

  &__trial p {
    @apply mt-3 text-md leading-6 text-neutral-700;
  }

  &__trial-button {
    @apply mt-6 flex h-buttons items-center justify-center gap-2 rounded-4xl bg-info-800 px-6;
    @apply text-buttons font-semibold text-white;

    box-shadow: 0 14px 34px -18px rgb(49 82 220 / 80%);

    &:hover,
    &:focus {
      @apply bg-info-900 text-white;
    }

    &:active {
      transform: translateY(2px);
    }
  }

  &__sign-in {
    @apply mt-10 border-t border-neutral-300 pt-9;
  }

  &__form-heading {
    @apply mb-7;
  }

  &__form-heading h3 {
    @apply m-0 text-xl font-medium text-neutral-700;
  }

  &__form-heading p {
    @apply mt-2 text-sm leading-5 text-neutral-600;
  }

  &__login-form {
    @apply mt-0 w-full;
  }

  &__legal {
    @apply mt-8 flex items-center justify-center gap-2 text-xs text-neutral-600;
  }

  &__legal a:hover,
  &__legal a:focus {
    @apply text-info-800;
  }
}

@keyframes conversation-cycle {
  0%,
  17% {
    opacity: 1;
    transform: translateY(0);
  }

  20%,
  100% {
    opacity: 0;
    transform: translateY(-8px);
  }
}

@media only screen and (max-width: 991px) {
  .mcp-auth {
    @apply block bg-neutral-200;

    &__story {
      @apply min-h-0;
    }

    &__story-content {
      @apply min-h-0 max-w-[760px] px-10 py-9;
    }

    &__copy {
      @apply pt-12;
    }

    &__title {
      @apply text-[38px];
    }

    &__conversation {
      @apply hidden;
    }

    &__value-props {
      @apply mb-0;
    }

    &__story-footer {
      @apply mt-7;
    }

    &__access {
      @apply px-8 py-[56px];
    }
  }
}

@media only screen and (max-width: 767px) {
  .mcp-auth {
    &__story-content {
      @apply px-5 py-8;
    }

    &__brand {
      @apply hidden;
    }

    &__copy {
      @apply pt-0;
    }

    &__eyebrow {
      @apply mb-3;
    }

    &__title {
      @apply text-[30px] leading-[1.15];
    }

    &__description {
      @apply mt-3 text-md leading-6;
    }

    &__value-props {
      @apply mt-5;
    }

    &__value-props-title {
      @apply text-md;
    }

    &__story-footer {
      @apply hidden;
    }

    &__access {
      @apply px-5 py-10;
    }

    &__mobile-brand {
      @apply mb-10 block;
    }

    &__trial h2 {
      @apply text-4xl;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .mcp-auth__conversation-turn {
    animation: none;
  }

  .mcp-auth__conversation-turn:first-child {
    @apply opacity-100;
  }
}
</style>
